const FAILURE_LABELS: Record<string, string> = {
  image_too_small: 'Image is too small for embedding',
  source_unavailable: 'Source image could not be downloaded',
  source_too_large: 'Source image exceeds the configured size limit',
  unsupported_image: 'Image format could not be decoded',
  image_empty: 'Image contains insufficient visible detail',
  normalization_failed: 'Image preparation failed',
  embedding_inference_failed: 'Embedding generation failed',
  persistence_failed: 'Embedding could not be saved',
  queue_dispatch_timeout: 'The worker did not begin processing the queued job',
  discovery_timeout: 'Catalog discovery stopped reporting progress',
  worker_inactive_timeout: 'The embedding worker stopped reporting progress',
};

const ACTIVITY_STATUS_LABELS: Record<string, string> = {
  pending: 'Waiting',
  processing: 'Processing',
  embedded: 'Embedded',
  skipped: 'Skipped',
  failed: 'Failed',
};

const PREPARATION_LABELS: Record<
  string,
  { label: string; description: string }
> = {
  trimmed: {
    label: 'Trimmed',
    description:
      'Uniform or transparent padding was removed from the temporary encoder input. The original image was not modified.',
  },
  unchanged: {
    label: 'Unchanged',
    description:
      'No safe crop was applied to the temporary encoder input. The original image was not modified.',
  },
};

export const humanizeEmbeddingKey = (value?: string | null) => {
  if (!value || typeof value !== 'string') return 'Unknown';
  return value.split('_').join(' ');
};

export const getEmbeddingFailureLabel = (code?: string | null) => {
  if (!code) return 'Embedding failed';
  return FAILURE_LABELS[code] || humanizeEmbeddingKey(code);
};

export const getEmbeddingActivityStatusLabel = (status?: string | null) => {
  if (!status) return 'Unknown';
  return ACTIVITY_STATUS_LABELS[status] || humanizeEmbeddingKey(status);
};

export const getEmbeddingPreparationPresentation = (
  outcome?: string | null
) => {
  if (!outcome) return null;
  return (
    PREPARATION_LABELS[outcome] || {
      label: humanizeEmbeddingKey(outcome),
      description: 'Temporary encoder preparation outcome.',
    }
  );
};

export const formatEmbeddingProcessingTime = (milliseconds?: number | null) => {
  const value = Number(milliseconds);
  if (!Number.isFinite(value) || value <= 0) return null;
  if (value < 1000) return `${Math.round(value)} ms`;
  return `${(value / 1000).toFixed(1)} s`;
};

export const formatEmbeddingRelativeTime = (
  value?: string | null,
  now = Date.now()
) => {
  if (!value) return 'unknown';
  const timestamp = new Date(value).getTime();
  if (!Number.isFinite(timestamp)) return 'unknown';
  const seconds = Math.max(0, Math.floor((now - timestamp) / 1000));
  if (seconds < 10) return 'a few seconds ago';
  if (seconds < 60) return `${seconds} seconds ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes === 1) return 'a minute ago';
  if (minutes < 60) return `${minutes} minutes ago`;
  const hours = Math.floor(minutes / 60);
  if (hours === 1) return 'an hour ago';
  if (hours < 24) return `${hours} hours ago`;
  const days = Math.floor(hours / 24);
  return days === 1 ? 'a day ago' : `${days} days ago`;
};

export const isEmbeddingActivityStale = (
  value?: string | null,
  now = Date.now()
) => {
  if (!value) return false;
  const timestamp = new Date(value).getTime();
  return Number.isFinite(timestamp) && now - timestamp > 2 * 60 * 1000;
};
